import { loader } from '@monaco-editor/react'

export const ESPER_LANGUAGE_ID = 'esper-epl'

export const ESPER_KEYWORDS = [
  // Core query clauses
  'select',
  'insert',
  'into',
  'from',
  'where',
  'group',
  'by',
  'having',
  'order',
  'limit',
  'offset',
  'distinct',
  'as',
  'output',
  'snapshot',
  'first',
  'last',
  'all',
  'after',
  'at',
  'every',
  'when',
  'then',
  'else',

  // Pattern matching
  'pattern',
  'match_recognize',
  'measures',
  'define',
  'partition',
  'matches',
  'one',
  'row',
  'per',

  // Operators and predicates
  'and',
  'or',
  'not',
  'in',
  'between',
  'like',
  'regexp',
  'is',
  'null',
  'exists',
  'any',
  'some',
  'within',

  // Stream modifiers
  'istream',
  'rstream',
  'irstream',
  'unidirectional',
  'retain-union',
  'retain-intersection',

  // Context and partitioning
  'context',
  'create',
  'start',
  'end',
  'initiated',
  'terminated',
  'partition',
  'coalesce',

  // Schema and data
  'schema',
  'variant',
  'table',
  'window',
  'variable',
  'expression',
  'index',
  'eventtype',

  // Data modification
  'update',
  'delete',
  'set',
  'on',

  // Control flow
  'case',
  'while',
  'until',
  'for',

  // Time and intervals
  'timer',
  'interval',
  'sec',
  'seconds',
  'min',
  'minutes',
  'hour',
  'hours',
  'day',
  'days',
  'msec',
  'milliseconds',

  // Type casting
  'cast',
  'typeof',
  'instanceof'
]

export const ESPER_OPERATORS = [
  '+', '-', '*', '/', '%', '=', '>', '<', '>=', '<=', '<>', '!=', '==',
  '||', // String concatenation
  '->', '=>', '@', // Pattern operators
  '?', ':', // Ternary operator
  '&', '|', '^', // Bitwise (rarely used but valid)
  'and', 'or', 'not', 'in', 'like'
]

export const ESPER_BUILTIN_FUNCTIONS = [
  // Aggregation functions
  'avg', 'count', 'max', 'min', 'sum', 'stddev', 'avedev', 'median', 'fmax', 'fmin', 'fmaxever', 'fminever',

  // Window functions
  'prev', 'prior', 'last', 'first', 'countever', 'sumever', 'maxever', 'minever',

  // String functions
  'substring', 'concat', 'lower', 'upper', 'trim', 'replace', 'length', 'indexOf', 'split', 'escape', 'unescape',

  // Math functions
  'abs', 'ceil', 'ceiling', 'floor', 'round', 'power', 'pow', 'sqrt', 'exp', 'ln', 'log', 'log10',
  'mod', 'sign',

  // Date/Time functions
  'current_timestamp', 'current_timestamp_ms', 'dateformat', 'dtget', 'dtset', 'dttostring', 'dtplus', 'dtminus',
  'dtround', 'toMillisec', 'getMinuteOfHour', 'getHourOfDay', 'getDayOfWeek', 'getDayOfMonth', 'getDayOfYear',
  'getMonthOfYear', 'getYear', 'getWeekYear', 'between',

  // Casting and type functions
  'cast', 'typeof', 'instanceof', 'exists', 'coalesce', 'isnumeric',

  // Collection functions
  'countof', 'sumof', 'minof', 'maxof', 'allof', 'anyof', 'takeof', 'takewhileof', 'takewhilelastof',
  'where', 'orderby', 'groupby', 'selectfrom', 'aggregate', 'distinctof', 'mostfrequent', 'leastfrequent',
  'firstof', 'lastof', 'reverseof', 'arraymax', 'arraymin', 'arraysum', 'arrayavg',

  // Data window and view functions
  'window', 'firstwindow', 'lastwindow',

  // Utility functions
  'case', 'regexp', 'like', 'inrange', 'instanceof', 'getbean',

  // JSON and nested access
  'json_exists', 'json_value', 'json_query', 'json_extract', 'json_arrayagg', 'json_objectagg'
]

const CLAUSE_SNIPPETS = [
  {
    label: 'SELECT Statement',
    insertText: 'select ${1:*} from ${2:EventStream}',
    documentation: 'Basic SELECT statement',
    kind: 15 // Snippet
  },
  {
    label: 'SELECT with WHERE',
    insertText: 'select ${1:*} from ${2:EventStream} where ${3:condition}',
    documentation: 'SELECT with filter condition',
    kind: 15
  },
  {
    label: 'SELECT with Time Window',
    insertText: 'select ${1:*} from ${2:EventStream}.win:time(${3:1 minute})',
    documentation: 'SELECT with time-based sliding window',
    kind: 15
  },
  {
    label: 'SELECT with Length Window',
    insertText: 'select ${1:*} from ${2:EventStream}.win:length(${3:100})',
    documentation: 'SELECT with length-based sliding window',
    kind: 15
  },
  {
    label: 'SELECT with Time Batch',
    insertText: 'select ${1:*} from ${2:EventStream}.win:time_batch(${3:1 minute})',
    documentation: 'SELECT with tumbling time window',
    kind: 15
  },
  {
    label: 'SELECT with GROUP BY',
    insertText: 'select ${1:field}, count(*) from ${2:EventStream}.win:time(${3:1 minute}) group by ${1:field}',
    documentation: 'SELECT with aggregation and grouping',
    kind: 15
  },
  {
    label: 'SELECT Pattern',
    insertText: 'select ${1:*} from pattern [every ${2:EventStream}]',
    documentation: 'Pattern-tracking SELECT with every operator',
    kind: 15
  },
  {
    label: 'Pattern Sequence',
    insertText: 'select ${1:*} from pattern [every ${2:A} -> ${3:B}]',
    documentation: 'Pattern matching event sequence',
    kind: 15
  },
  {
    label: 'Pattern with Timer',
    insertText: 'select ${1:*} from pattern [every ${2:A} -> (timer:interval(${3:5 sec}) and not ${4:B})]',
    documentation: 'Pattern with timeout detection',
    kind: 15
  },
  {
    label: 'INSERT INTO',
    insertText: 'insert into ${1:TargetStream} select ${2:*} from ${3:SourceStream}',
    documentation: 'Insert results into another stream',
    kind: 15
  },
  {
    label: 'INSERT INTO with ISTREAM',
    insertText: 'insert istream into ${1:TargetStream} select ${2:*} from ${3:SourceStream}',
    documentation: 'Insert insert-stream events only',
    kind: 15
  },
  {
    label: 'CREATE WINDOW',
    insertText: 'create window ${1:WindowName}.win:time(${2:1 minute}) as ${3:EventType}',
    documentation: 'Create a named window',
    kind: 15
  },
  {
    label: 'CONTEXT Partition',
    insertText: 'create context ${1:ContextName} partition by ${2:field} from ${3:EventStream}',
    documentation: 'Create partitioned context',
    kind: 15
  },
  {
    label: 'OUTPUT Snapshot',
    insertText: 'select ${1:*} from ${2:EventStream}.win:time(${3:1 minute}) output snapshot every ${4:10 seconds}',
    documentation: 'Periodic snapshot output',
    kind: 15
  }
]

let languageRegistered = false

// Use CDN instead of bundling Monaco locally (saves ~36MB of static files)
// Using jsDelivr CDN with specific version for reliability
const MONACO_VERSION = '0.54.0' // Match the version in package.json
const MONACO_CDN_URL = `https://cdn.jsdelivr.net/npm/monaco-editor@${MONACO_VERSION}/min/vs`

// Configure Monaco loader before initialization
loader.config({
  paths: {
    vs: MONACO_CDN_URL
  },
  'vs/nls': {
    availableLanguages: {}
  }
})

// Override the loader's init to return window.monaco. @monaco-editor/react CANCELS the promise
// init returns when an editor unmounts; an async override returned a plain Promise, so every
// unmount threw "cancel is not a function" (and the statements layout tests failed on it).
// The returned promise forwards cancel to the loader's own cancelable promise.
const originalInit = loader.init
loader.init = () => {
  const underlying = originalInit()
  const ready = (async () => {
    await underlying
    // Wait for Monaco to be available on window
    let attempts = 0
    while (!window.monaco && attempts < 50) {
      await new Promise(resolve => setTimeout(resolve, 100))
      attempts++
    }
    if (!window.monaco) {
      throw new Error('Monaco failed to load on window.monaco')
    }
    console.log('[monacoSetup] Loader init returning window.monaco')
    return window.monaco
  })()
  ready.cancel = () => underlying.cancel?.()
  return ready
}

// Enable Monaco to load from CDN by setting the Monaco environment
if (typeof window !== 'undefined') {
  window.MonacoEnvironment = {
    getWorkerUrl: function (moduleId, label) {
      // Return the worker script URL from CDN
      if (label === 'json') {
        return `${MONACO_CDN_URL}/language/json/json.worker.js`
      }
      if (label === 'css' || label === 'scss' || label === 'less') {
        return `${MONACO_CDN_URL}/language/css/css.worker.js`
      }
      if (label === 'html' || label === 'handlebars' || label === 'razor') {
        return `${MONACO_CDN_URL}/language/html/html.worker.js`
      }
      if (label === 'typescript' || label === 'javascript') {
        return `${MONACO_CDN_URL}/language/typescript/ts.worker.js`
      }
      return `${MONACO_CDN_URL}/editor/editor.worker.js`
    }
  }
}

const registerLanguage = (monaco) => {
  if (languageRegistered) {
    return
  }

  if (!monaco.languages.getLanguages().some((lang) => lang.id === ESPER_LANGUAGE_ID)) {
    monaco.languages.register({ id: ESPER_LANGUAGE_ID })
  }

  monaco.languages.setMonarchTokensProvider(ESPER_LANGUAGE_ID, {
    defaultToken: '',
    ignoreCase: true,
    brackets: [
      { open: '[', close: ']', token: 'delimiter.square' },
      { open: '{', close: '}', token: 'delimiter.curly' },
      { open: '(', close: ')', token: 'delimiter.parenthesis' }
    ],
    keywords: ESPER_KEYWORDS,
    operators: ESPER_OPERATORS,
    tokenizer: {
      root: [
        // Window and view namespaces like .win:, .std:, .ext:
        [/\.(?:win|std|ext|uni):/, 'delimiter.namespace'],

        // Pattern operators
        [/->|=>|@/, 'operator'],

        // Keywords and identifiers
        [/[a-zA-Z_][\w$]*/, { cases: { '@keywords': 'keyword', '@builtins': 'predefined', '@default': 'identifier' } }],

        { include: '@whitespace' },

        // Brackets
        [/[{}()[\]]/, '@brackets'],

        // Operators (expanded to include ||, etc.)
        [/\|\||<>|!=|==|<=|>=|[<>=!%+\-*/&|^?:]/, 'operator'],

        // Numbers
        [/\d*\.\d+([eE][-+]?\d+)?[fFdD]?/, 'number.float'],
        [/\d+[lL]?/, 'number'],

        // Strings
        [/'([^'\\]|\\.)*$/, 'string.invalid'],
        [/'/, 'string', '@string'],
        [/"/, 'string', '@dblString']
      ],
      string: [
        [/[^\\']+/, 'string'],
        [/\\./, 'string.escape'],
        [/'/, 'string', '@pop']
      ],
      dblString: [
        [/[^\\"]+/, 'string'],
        [/\\./, 'string.escape'],
        [/"/, 'string', '@pop']
      ],
      whitespace: [
        [/\s+/, 'white'],
        [/--+.*/, 'comment'],
        [/\/\*/, 'comment', '@blockComment']
      ],
      blockComment: [
        [/[^/*]+/, 'comment'],
        [/\*\//, 'comment', '@pop'],
        [/[/*]/, 'comment']
      ]
    },
    builtins: ESPER_BUILTIN_FUNCTIONS
  })

  monaco.languages.setLanguageConfiguration(ESPER_LANGUAGE_ID, {
    comments: { lineComment: '--', blockComment: ['/*', '*/'] },
    autoClosingPairs: [
      { open: '"', close: '"', notIn: ['string'] },
      { open: "'", close: "'", notIn: ['string'] },
      { open: '(', close: ')' },
      { open: '[', close: ']' },
      { open: '{', close: '}' }
    ],
    folding: {
      markers: {
        start: /^\s*--\s*#region\b/,
        end: /^\s*--\s*#endregion\b/
      }
    }
  })

  monaco.editor.defineTheme('esper-dark', {
    base: 'vs-dark',
    inherit: true,
    rules: [
      { token: 'keyword', foreground: '5DBEFF', fontStyle: 'bold' },
      { token: 'predefined', foreground: '9CDCFE' },
      { token: 'string', foreground: 'CE9178' },
      { token: 'number', foreground: 'B5CEA8' },
      { token: 'comment', foreground: '6A9955', fontStyle: 'italic' },
      { token: 'operator', foreground: 'FFB454' },
      { token: 'delimiter.namespace', foreground: 'D4D4D4' } // For .win:, .std:
    ],
    colors: {
      'editor.background': '#0d1117',
      'editorLineNumber.foreground': '#4B5263',
      'editorCursor.foreground': '#71EFFF',
      'editor.selectionBackground': '#264f78'
    }
  })

  // Note: Completion provider is registered per-editor in StatementEditor.jsx
  // to integrate with dynamic schema and event samples. This avoids duplicates.

  languageRegistered = true
}

// Export snippet templates for use in StatementEditor completion provider
export const getSnippetCompletions = () => CLAUSE_SNIPPETS

export const initializeMonaco = async () => {
  try {
    console.log('[monacoSetup] Initializing Monaco from CDN:', MONACO_CDN_URL)

    // Let @monaco-editor/react load Monaco
    await loader.init()

    // Monaco exposes itself on window.monaco when loaded via AMD
    // Wait a bit for it to be available
    let attempts = 0
    while (!window.monaco && attempts < 50) {
      await new Promise(resolve => setTimeout(resolve, 100))
      attempts++
    }

    if (!window.monaco) {
      throw new Error('Monaco failed to load on window.monaco after 5 seconds - check Network tab for failed CDN requests')
    }

    const monaco = window.monaco

    if (!monaco.languages) {
      throw new Error('Monaco loaded but languages API is missing')
    }

    registerLanguage(monaco)
    console.log('[monacoSetup] Monaco initialized successfully from window.monaco')
    return monaco
  } catch (error) {
    console.error('[monacoSetup] Failed to initialize Monaco:', error)
    console.error('[monacoSetup] Check browser Network tab for failed requests to:', MONACO_CDN_URL)
    throw error
  }
}

export const withMonaco = async (callback) => {
  const monaco = await initializeMonaco()
  return callback(monaco)
}

export const ensureEsperLanguage = (monaco) => {
  registerLanguage(monaco)
  return monaco
}
